import { useState, useCallback, useMemo, useEffect } from "react";
import ReactFlow, { Background, Controls, useNodesState, useEdgesState } from "reactflow";
import "reactflow/dist/style.css";
import { useAuth } from "@/contexts/AuthContext";
import { MagnifyingGlassMinus, MagnifyingGlassPlus, ArrowsClockwise } from "@phosphor-icons/react";

const DEPT_COLORS = {
  "Vendite": "#1B4FD8", "Marketing": "#8B5CF6", "IT": "#10B981", "HR": "#F59E0B",
  "Amministrazione": "#EC4899", "Logistica": "#14B8A6", "Produzione": "#EF4444",
  "Finanza": "#6366F1", "default": "#0D1F3C"
};

const SCALE_OPTIONS = [0.5, 0.75, 1.0, 1.25];
const SCALE_KEY = "selly_orgchart_scale";

function EmployeeNode({ data }) {
  const color = DEPT_COLORS[data.department] || DEPT_COLORS.default;
  const s = data.scale || 1;
  return (
    <div className="rounded-xl border-2 bg-white shadow-lg text-center transition-all hover:shadow-xl"
      style={{
        borderColor: color,
        padding: `${12 * s}px ${16 * s}px`,
        minWidth: `${160 * s}px`,
      }}
      data-testid={`org-node-${data.id}`}>
      <div className="rounded-full mx-auto flex items-center justify-center text-white font-bold"
        style={{
          backgroundColor: color,
          width: `${40 * s}px`, height: `${40 * s}px`,
          fontSize: `${14 * s}px`, marginBottom: `${8 * s}px`,
        }}>
        {data.first_name?.[0]}{data.last_name?.[0]}
      </div>
      <p className="font-semibold text-[#0D1F3C] leading-tight" style={{ fontSize: `${14 * s}px` }}>{data.first_name} {data.last_name}</p>
      <p className="text-[#0D1F3C]/60" style={{ fontSize: `${10 * s}px`, marginTop: `${2 * s}px` }}>{data.job_title}</p>
      <p className="font-medium rounded-full inline-block"
        style={{
          fontSize: `${10 * s}px`,
          padding: `${2 * s}px ${8 * s}px`,
          marginTop: `${4 * s}px`,
          backgroundColor: `${color}15`, color,
        }}>
        {data.department}
      </p>
      {data.is_manager && <p className="text-[#F59E0B] font-medium" style={{ fontSize: `${10 * s}px`, marginTop: `${4 * s}px` }}>Responsabile</p>}
    </div>
  );
}

const nodeTypes = { employee: EmployeeNode };

export default function OrgChart({ employees, companyName: companyNameProp }) {
  const { user } = useAuth();
  // BUG-29: usa il nome azienda dell'utente loggato invece di "Azienda" hardcoded
  const companyName = companyNameProp || user?.active_workspace_name || user?.company_name || "Azienda";

  // BUG-30: scala nodi (50% / 75% / 100% / 125%) con preferenza salvata
  const [scale, setScale] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem(SCALE_KEY));
      return SCALE_OPTIONS.includes(v) ? v : 1.0;
    } catch { return 1.0; }
  });
  useEffect(() => { try { localStorage.setItem(SCALE_KEY, String(scale)); } catch {} }, [scale]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useMemo(() => {
    if (!employees || employees.length === 0) { setNodes([]); setEdges([]); return; }

    const managers = employees.filter(e => e.is_manager);
    const nonManagers = employees.filter(e => !e.is_manager);

    const newNodes = [];
    const newEdges = [];

    // Company root node (BUG-29)
    newNodes.push({
      id: "company",
      type: "default",
      position: { x: 400, y: 0 },
      data: { label: companyName },
      style: {
        background: "#0D1F3C", color: "#fff",
        borderRadius: 12 * scale,
        padding: `${10 * scale}px ${24 * scale}px`,
        fontWeight: 700,
        fontSize: 14 * scale,
        border: "none",
      },
    });
    let yOffset = 100 * scale + 30;

    const managerSpacing = 220 * scale;
    const managerStartX = 400 - ((managers.length - 1) * managerSpacing) / 2;

    managers.forEach((m, i) => {
      const nodeId = `emp-${m.id}`;
      newNodes.push({
        id: nodeId,
        type: "employee",
        position: { x: managerStartX + i * managerSpacing, y: yOffset },
        data: { ...m, scale },
      });
      newEdges.push({ id: `e-company-${nodeId}`, source: "company", target: nodeId, type: "smoothstep", animated: false, style: { stroke: "#0D1F3C30", strokeWidth: 2 } });
    });

    yOffset += 180 * scale;

    if (nonManagers.length > 0) {
      const empSpacing = 200 * scale;
      const empStartX = 400 - ((nonManagers.length - 1) * empSpacing) / 2;

      nonManagers.forEach((emp, i) => {
        const nodeId = `emp-${emp.id}`;
        newNodes.push({
          id: nodeId,
          type: "employee",
          position: { x: empStartX + i * empSpacing, y: yOffset },
          data: { ...emp, scale },
        });
        // Link to nearest department manager if exists
        const deptManager = managers.find(m => m.department === emp.department);
        if (deptManager) {
          newEdges.push({ id: `e-mgr-${nodeId}`, source: `emp-${deptManager.id}`, target: nodeId, type: "smoothstep", animated: false, style: { stroke: `${DEPT_COLORS[emp.department] || DEPT_COLORS.default}40`, strokeWidth: 1.5 } });
        } else {
          newEdges.push({ id: `e-root-${emp.id}`, source: "company", target: nodeId, type: "smoothstep", animated: false, style: { stroke: "#0D1F3C20", strokeWidth: 1.5 } });
        }
      });
    }

    setNodes(newNodes);
    setEdges(newEdges);
  }, [employees, scale, companyName, setNodes, setEdges]);

  const dec = useCallback(() => {
    const idx = SCALE_OPTIONS.indexOf(scale);
    if (idx > 0) setScale(SCALE_OPTIONS[idx - 1]);
  }, [scale]);
  const inc = useCallback(() => {
    const idx = SCALE_OPTIONS.indexOf(scale);
    if (idx >= 0 && idx < SCALE_OPTIONS.length - 1) setScale(SCALE_OPTIONS[idx + 1]);
  }, [scale]);
  const reset = useCallback(() => setScale(1.0), []);

  return (
    <div className="relative w-full h-[600px]" data-testid="org-chart">
      {/* BUG-30: Zoom controls */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-white border border-[#0D1F3C]/10 rounded-lg shadow-sm p-1">
        <button onClick={dec} disabled={scale === SCALE_OPTIONS[0]}
          className="p-1.5 rounded hover:bg-[#1B4FD8]/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-[#1B4FD8]"
          title="Riduci nodi" data-testid="org-zoom-out">
          <MagnifyingGlassMinus size={16} weight="bold" />
        </button>
        <span className="text-xs font-medium text-[#0D1F3C]/70 px-2 min-w-[42px] text-center" data-testid="org-zoom-label">
          {Math.round(scale * 100)}%
        </span>
        <button onClick={inc} disabled={scale === SCALE_OPTIONS[SCALE_OPTIONS.length - 1]}
          className="p-1.5 rounded hover:bg-[#1B4FD8]/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-[#1B4FD8]"
          title="Ingrandisci nodi" data-testid="org-zoom-in">
          <MagnifyingGlassPlus size={16} weight="bold" />
        </button>
        <div className="w-px h-5 bg-[#0D1F3C]/10 mx-0.5" />
        <button onClick={reset} disabled={scale === 1.0}
          className="p-1.5 rounded hover:bg-[#1B4FD8]/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-[#0D1F3C]/60"
          title="Reimposta dimensione (100%)" data-testid="org-zoom-reset">
          <ArrowsClockwise size={14} weight="bold" />
        </button>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
      >
        <Background color="#0D1F3C" gap={20} size={0.5} />
        <Controls position="bottom-right" />
      </ReactFlow>
    </div>
  );
}
